import { useState, useCallback } from 'react';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Handler = (...args: any[]) => Promise<any>;

interface UseStatefulHandlerParams<THandler extends Handler, O extends boolean> {
    handler: THandler;
    onError?: (e: Error) => void;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onLoad?: THandler extends (...args: any[]) => Promise<infer R> ? (data: R) => void : never;
    afterLoad?: () => void;
    throwErrors?: boolean;
    omitResult?: O;
}

export type FetchingHandlerState = 'loading' | 'error' | 'idle';

export function useFetchingHandler<THandler extends Handler, O extends boolean = false>({
    handler,
    onError,
    onLoad,
    omitResult,
    afterLoad,
    throwErrors = false,
}: UseStatefulHandlerParams<THandler, O>) {
    const [state, setState] = useState<FetchingHandlerState>('idle');

    const fetchingHandler = useCallback(
        async (...args: Parameters<THandler>) => {
            if (state === 'loading') {
                return;
            }

            setState('loading');

            let data: ReturnType<THandler> extends Promise<infer R> ? R : never;

            try {
                data = await handler(...args);
                onLoad && onLoad(data);
            } catch (e) {
                setState('error');
                console.error(e);
                if (onError) {
                    const fetchError = new Error(String(e));
                    onError(fetchError);
                }

                if (throwErrors) {
                    throw e;
                }

                return;
            }

            setState('idle');

            afterLoad && afterLoad();

            return omitResult ? undefined : data;
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [handler, onLoad, onError, omitResult, afterLoad, throwErrors],
    );

    const initHandlerState = useCallback(() => {
        setState('idle');
    }, [setState]);

    return [
        fetchingHandler as (...args: Parameters<THandler>) => O extends true ? Promise<void> : ReturnType<THandler>,
        state,
        initHandlerState,
    ] as const;
}
